import type { Broadcast } from "@tdelivr/api-client/generated/notifications/notification-orchestrator.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { at, field, absent, type RecordDetail } from "./record-detail.ts";

/**
 * What the broadcasts board does to the rows NTF-01 answers, away from any rendering.
 *
 * **This page did not exist until 22 October, and the reason was the subject.** The design cites
 * NTF-04, the push adapter, whose `push_attempts` is one row per message *per device* with a status
 * that ends at `delivered`. The board draws `Message · Audience · Sent` over `Road closure on
 * Waiyaki Way · Westlands · 64 drivers` — one message to many people. No row on this platform was
 * that, so a listing of push attempts would have filled two of three columns with per-device rows
 * and left out the column that makes a broadcast a broadcast.
 *
 * Three things about what is drawn:
 *
 * **The audience is a rule, so the cell reads the rule and not a roster.** NTF-01 stores the zones
 * and service classes the operator chose, because the couriers matching them change minute by
 * minute — a broadcast scheduled for 13:00 is for whoever is on shift then. `64 drivers` is the
 * count once resolution has run, and before that the cell says what the rule is instead.
 *
 * **The read rate is undefined rather than zero where it cannot be known.** NTF-05 models
 * `unread · read · dismissed`; NTF-04's push status ends at `delivered`. A broadcast that went out
 * only as a push has no read rate, and `0%` would say every courier ignored it.
 *
 * **Nothing has been sent yet, so every count is null.** The fan-out — resolving an audience across
 * DRV-05 presence and GEO-06 areas, and holding a message until a courier is out of a trip, which
 * the design's own note requires — is not built. The board draws the absence rather than a zero,
 * because a broadcast that has not gone out has not reached nobody.
 */

export type BroadcastState = Broadcast["state"];

const BADGE: Readonly<
  Record<BroadcastState, { readonly classKey: StateClassKey; readonly label: string }>
> = {
  scheduled: { classKey: "waiting", label: "scheduled" },
  sending: { classKey: "active", label: "sending" },
  sent: { classKey: "done", label: "delivered" },
  failed: { classKey: "failed", label: "failed" },
};

export function badgeFor(state: BroadcastState): {
  readonly classKey: StateClassKey;
  readonly label: string;
} {
  return BADGE[state];
}

/**
 * The design's `Message` column: the English body, which is the one always present.
 *
 * Truncated here rather than by CSS, so the title can carry the whole of it and every locale the
 * operator wrote. A message clipped by overflow is a message an operator cannot read at all.
 */
export function messageCell(broadcast: Broadcast): {
  readonly text: string;
  readonly title: string;
} {
  const english = broadcast.body["en"] ?? "";
  const locales = Object.keys(broadcast.body).sort();
  return {
    text: english.length > 60 ? `${english.slice(0, 59)}…` : english,
    title: locales.map((locale) => `${locale}: ${broadcast.body[locale] ?? ""}`).join("\n"),
  };
}

/**
 * The design's `Audience` column: `Westlands · 64 drivers`, or the rule where no count exists.
 *
 * Both lists empty is the design's own `All` chip, and is drawn as that word rather than as an
 * empty cell — an audience of everybody is a choice, not a blank.
 */
export function audienceCell(broadcast: Broadcast): {
  readonly text: string;
  readonly title: string;
} {
  const { zones, serviceClasses } = broadcast.audience;
  const where = zones.length === 0 ? "All zones" : zones.join(", ");
  const who = serviceClasses.length === 0 ? "" : ` · ${serviceClasses.join(", ")}`;
  const count =
    broadcast.recipients === null
      ? ""
      : ` · ${String(broadcast.recipients)} ${broadcast.recipients === 1 ? "driver" : "drivers"}`;
  return {
    text: `${where}${who}${count}`,
    title:
      broadcast.recipients === null
        ? "The audience is a rule rather than a list, and it has not been resolved yet. The couriers matching it change minute by minute, so it is resolved when the broadcast goes out."
        : `${String(broadcast.recipients)} couriers matched this rule when the broadcast went out.`,
  };
}

/**
 * What share of the deliveries were read, or `undefined` where that cannot be known.
 *
 * The console's own copy of the rule NTF-01's domain states, restated here rather than imported
 * because a console must not import a service's source — and asserted against the published fields
 * in `tests/broadcasts.test.ts` so it cannot drift from it silently.
 */
export function readRate(broadcast: Broadcast): number | undefined {
  if (broadcast.readInApp === null || broadcast.delivered === null) return undefined;
  if (broadcast.delivered === 0) return undefined;
  return broadcast.readInApp / broadcast.delivered;
}

/** The four tiles, over every row rather than the filtered page. */
export interface BroadcastTiles {
  readonly sent: number;
  readonly scheduled: number;
  readonly failed: number;
  /** The share read across every broadcast that can report one, or `undefined` where none can. */
  readonly readRate: number | undefined;
}

export function tilesFor(broadcasts: readonly Broadcast[]): BroadcastTiles {
  const reportable = broadcasts.filter(
    (broadcast) => broadcast.readInApp !== null && broadcast.delivered !== null,
  );
  const delivered = reportable.reduce((total, broadcast) => total + (broadcast.delivered ?? 0), 0);
  const read = reportable.reduce((total, broadcast) => total + (broadcast.readInApp ?? 0), 0);

  return {
    sent: broadcasts.filter((broadcast) => broadcast.state === "sent").length,
    scheduled: broadcasts.filter((broadcast) => broadcast.state === "scheduled").length,
    failed: broadcasts.filter((broadcast) => broadcast.state === "failed").length,
    /*
     * Undefined where nothing can report, which is the design's `88%` drawn as an absence. Summing
     * the parts rather than averaging the rates, because a broadcast to 600 couriers and one to 6
     * are not two equal opinions about how well broadcasts are read.
     */
    readRate: reportable.length === 0 || delivered === 0 ? undefined : read / delivered,
  };
}

/** One broadcast's detail panel. */
export function broadcastDetail(broadcast: Broadcast): RecordDetail {
  const badge = badgeFor(broadcast.state);
  const rate = readRate(broadcast);
  const audience = audienceCell(broadcast);

  return {
    headline: broadcast.body["en"] ?? broadcast.broadcastId,
    identifier: broadcast.broadcastId,
    source: "NTF-01",
    state: {
      classKey: badge.classKey,
      label: badge.label,
      code: "NTF-01",
      status: broadcast.state,
    },
    fields: [
      ...Object.keys(broadcast.body)
        .sort()
        .map((locale) => field(locale.toUpperCase(), broadcast.body[locale] ?? "")),
      field("Audience", audience.text),
      broadcast.scheduledFor === null
        ? field("Send", "Immediately")
        : field("Scheduled for", at(broadcast.scheduledFor) ?? broadcast.scheduledFor),
      broadcast.recipients === null
        ? absent(
            "Couriers matched",
            "The audience is a rule and has not been resolved. It is resolved when the broadcast goes out, because the couriers on shift change minute by minute.",
          )
        : field("Couriers matched", String(broadcast.recipients)),
      broadcast.delivered === null
        ? absent(
            "Delivered",
            "No adapter has reported yet. The fan-out that sends a broadcast to each matched courier is not built.",
          )
        : field("Delivered", String(broadcast.delivered)),
      rate === undefined
        ? absent(
            "Read",
            "A read is a fact only for the in-app channel: NTF-05 models unread, read and dismissed, and NTF-04's push status ends at delivered. A push-only broadcast has no read rate, and 0% would say every courier ignored it.",
          )
        : field("Read", `${(rate * 100).toFixed(0)}%`),
      field("Created", at(broadcast.createdAt) ?? broadcast.createdAt),
      field("Version", `v${String(broadcast.version)}`),
    ],
  };
}
